// src/main.ts
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createI18n } from 'vue-i18n'
import router from './router'

import { useTranslationStore } from '@/stores/useTranslationStore'
import { useAuthStore } from '@/stores/useAuthStore'
import useCalaUser from '@/composables/UseCalaUser'

// CSS
import 'bootstrap/dist/css/bootstrap-reboot.css'
import '@/scss/settings/_fonts.scss'
import '@/scss/settings/_variables.scss'
import '@/scss/_customStyles.scss'

// Vuetify
import 'vuetify/styles'
import '@mdi/font/css/materialdesignicons.css'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
import { aliases, mdi } from 'vuetify/iconsets/mdi'
import DateFnsAdapter from '@date-io/date-fns'
import { enUS, is } from 'date-fns/locale'
import type { VueMessageType } from 'vue-i18n'

// import { en } from 'vuetify/locale'
import App from './App.vue'

function applyBodyDarkClass(enabled: boolean) {
  document.body.classList.toggle('dark-mode', enabled)
}

// Escape @ symbols in translation strings to prevent i18n compilation errors
function escapeTranslationMessages(messages: any): any {
  if (typeof messages === 'string') {
    // Replace @ with a placeholder that won't trigger i18n parser, then we'll restore it on display
    return messages.replace(/@/g, '__AT__')
  }
  if (Array.isArray(messages)) {
    return messages.map(escapeTranslationMessages)
  }
  if (messages && typeof messages === 'object') {
    const escaped: any = {}
    for (const key in messages) {
      escaped[key] = escapeTranslationMessages(messages[key])
    }
    return escaped
  }
  return messages
}

async function bootstrapApp() {
  const app = createApp(App)
  const pinia = createPinia()
  app.use(pinia)

  // Initialize auth early (will be used by router guard)
  const authStore = useAuthStore()
  await authStore.getAuthUser().catch((e) => {
    console.warn('Auth init failed (continuing):', e)
  })

  // Only fetch dark mode preference if user is logged in
  let preferDark = false
  if (authStore.isLoggedIn) {
    const { getCalaUser } = useCalaUser()
    try {
      const resp = await getCalaUser()
      let customer: any = resp?.user
      if (!customer) {
        customer = resp?.item1 ?? (Array.isArray(resp) ? resp[0] : null)
      }
      preferDark = !!customer?.preferDarkMode
    } catch (e: any) {
      console.warn('Cala user fetch failed, using default theme:', e)
    }
  }
  applyBodyDarkClass(preferDark)

  const vuetify = createVuetify({
    components,
    directives,
    defaults: {
      global: {
        style: { fontFamily: "'FFUnitWebRegular', sans-serif" },
      },
    },
    icons: {
      defaultSet: 'mdi',
      aliases,
      sets: { mdi },
    },
    theme: {
      defaultTheme: preferDark ? 'dark' : 'light',
      themes: {
        light: { dark: false },
        dark: { dark: true },
      },
    },
    date: {
      adapter: DateFnsAdapter,
      locale: {
        en: enUS,
        'en-US': enUS,
        is,
      },
    },
  })

  const i18n = createI18n({
    legacy: false,
    locale: 'isl',
    messages: {},
    missing(locale, key) {
      console.warn(`[i18n] Missing translation for key: ${key} in locale: ${locale}`)
      return key
    },
    postTranslation(str) {
      // Restore @ symbols that were escaped to prevent compilation errors
      if (typeof str === 'string') {
        return str.replace(/__AT__/g, '@')
      }
      return str
    },
  })

  const translationStore = useTranslationStore()
  try {
    await translationStore.setTranslations()
    const messages = translationStore.translations
    for (const locale in messages) {
      // Escape @ symbols before passing to i18n compiler
      const escapedMessages = escapeTranslationMessages(messages[locale])
      i18n.global.setLocaleMessage(locale, escapedMessages)
    }
  } catch (err) {
    console.error('Error loading translations:', err)
  }

  app.use(i18n)
  app.use(router)
  app.use(vuetify)

  await router.isReady()
  app.mount('#app')
}

bootstrapApp()
